import { useEffect, useState } from "react";
import { fetchHistorico, fetchHistoricoConsultoras, fetchSnapshots, deleteSnapshot } from "../lib/api";
import { formatEUR } from "../lib/format";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from "recharts";
import { Trash, TrendUp, TrendDown, Minus } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useSnapshot } from "../contexts/SnapshotContext";

const RANK_ORDER = ["Consultor", "Lider Junior", "Lider Ejecutivo", "Lider Master"];
const RANK_COLORS = {
  Consultor: "text-[#736F68] bg-[#F2EFEA]",
  "Lider Junior": "text-[#92620A] bg-[#FEF3C7]",
  "Lider Ejecutivo": "text-[#7C3626] bg-[#FDECEA]",
  "Lider Master": "text-[#3D6B39] bg-[#DCFCE7]",
};
const RANK_SHORT = {
  Consultor: "C",
  "Lider Junior": "LJ",
  "Lider Ejecutivo": "LE",
  "Lider Master": "LM",
};

function RankPill({ rank }) {
  if (!rank) return <span className="text-[#B0A89E] text-xs">—</span>;
  return (
    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${RANK_COLORS[rank] || ""}`}>
      {RANK_SHORT[rank] || rank}
    </span>
  );
}

function Trend({ values }) {
  if (values.length < 2) return null;
  const last = values[values.length - 1];
  const prev = values[values.length - 2];
  if (last > prev) return <TrendUp size={13} className="text-[#8A9A86] inline" weight="bold" />;
  if (last < prev) return <TrendDown size={13} className="text-[#B85C5C] inline" weight="bold" />;
  return <Minus size={13} className="text-[#B0A89E] inline" weight="bold" />;
}

export default function Historico() {
  const [series, setSeries] = useState([]);
  const [snapshots, setSnapshots] = useState([]);
  const [consultoras, setConsultoras] = useState({ periods: [], consultoras: [] });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");

  const { selected } = useSnapshot();

  const load = async () => {
    setLoading(true);
    const params = selected ? { coordenador: selected.coordenador } : {};
    const [h, snaps, cons] = await Promise.all([
      fetchHistorico(params),
      fetchSnapshots(params),
      fetchHistoricoConsultoras(params),
    ]);
    setSeries(h.series || []);
    setSnapshots(snaps || []);
    setConsultoras(cons || { periods: [], consultoras: [] });
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, [selected]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDelete = async (id, period) => {
    await deleteSnapshot(id);
    toast.success(`Snapshot ${period} eliminado`);
    load();
  };

  // Filtro de fechas
  const filteredSeries = series.filter((s) => {
    if (desde && s.period < desde) return false;
    if (hasta && s.period > hasta) return false;
    return true;
  });

  const filteredPeriods = consultoras.periods.filter((p) => {
    if (desde && p < desde) return false;
    if (hasta && p > hasta) return false;
    return true;
  });

  const filteredConsultoras = consultoras.consultoras.filter((c) =>
    c.nome.toLowerCase().includes(search.toLowerCase())
  );

  const applyQuick = (months) => {
    if (!months) { setDesde(""); setHasta(""); return; }
    const d = new Date();
    d.setMonth(d.getMonth() - months + 1);
    setDesde(d.toISOString().slice(0, 7));
    setHasta("");
  };

  const periods = filteredPeriods;

  return (
    <div className="p-6 md:p-10 space-y-8" data-testid="historico-page">
      <header>
        <p className="text-xs tracking-widest uppercase text-[#736F68] font-display font-semibold">
          Evolución
        </p>
        <h1 className="mt-1 text-3xl md:text-4xl font-semibold font-display text-[#2D2A26]">
          Histórico mensual
        </h1>
        <p className="mt-1 text-[#736F68] text-sm">
          Evolución del equipo periodo a periodo.
        </p>
      </header>

      {/* Filtros de fecha */}
      {!loading && series.length > 0 && (
        <div className="bg-white border border-[#E5E0D8] rounded-xl px-5 py-4 flex flex-wrap items-center gap-4">
          <span className="text-xs font-semibold uppercase tracking-wider text-[#736F68]">Periodo</span>
          <div className="flex items-center gap-2">
            <input
              type="month"
              value={desde}
              onChange={(e) => setDesde(e.target.value)}
              className="text-sm border border-[#E5E0D8] rounded-lg px-2.5 py-1.5 bg-[#F9F8F6] focus:outline-none focus:ring-1 focus:ring-[#C57B67]"
              placeholder="Desde"
            />
            <span className="text-[#B0A89E] text-xs">—</span>
            <input
              type="month"
              value={hasta}
              onChange={(e) => setHasta(e.target.value)}
              className="text-sm border border-[#E5E0D8] rounded-lg px-2.5 py-1.5 bg-[#F9F8F6] focus:outline-none focus:ring-1 focus:ring-[#C57B67]"
              placeholder="Hasta"
            />
          </div>
          <div className="flex gap-1.5">
            {[{l:"3m",m:3},{l:"6m",m:6},{l:"1 año",m:12},{l:"Todo",m:null}].map(({l,m}) => (
              <button
                key={l}
                onClick={() => applyQuick(m)}
                className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${
                  (m === null && !desde && !hasta) ||
                  (m !== null && desde === (() => { const d=new Date(); d.setMonth(d.getMonth()-m+1); return d.toISOString().slice(0,7); })() && !hasta)
                    ? "bg-[#C57B67] text-white"
                    : "bg-[#F2EFEA] text-[#736F68] hover:bg-[#E5E0D8]"
                }`}
              >
                {l}
              </button>
            ))}
          </div>
          {(desde || hasta) && (
            <span className="text-xs text-[#736F68]">
              {filteredSeries.length} periodo{filteredSeries.length !== 1 ? "s" : ""}
            </span>
          )}
        </div>
      )}

      {loading ? (
        <p className="text-[#736F68]">Cargando…</p>
      ) : series.length === 0 ? (
        <div className="bg-white border border-[#E5E0D8] rounded-xl p-8 text-center text-[#736F68]">
          Aún no hay datos históricos. Sube al menos dos CSVs de periodos distintos para ver la evolución.
        </div>
      ) : filteredSeries.length === 0 ? (
        <div className="bg-white border border-[#E5E0D8] rounded-xl p-8 text-center text-[#736F68]">
          Ningún periodo coincide con el filtro seleccionado.
        </div>
      ) : (
        <>
          {/* Gráfico ventas y comisiones */}
          <div className="bg-white border border-[#E5E0D8] rounded-xl p-6">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-[#736F68] font-display">
              Ventas y comisiones
            </h3>
            <div className="mt-4 h-72">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={filteredSeries}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#EFEAE1" />
                  <XAxis dataKey="period" stroke="#736F68" fontSize={11} />
                  <YAxis stroke="#736F68" fontSize={11} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                  <Tooltip
                    contentStyle={{ background: "#fff", border: "1px solid #E5E0D8", borderRadius: 8, fontSize: 12 }}
                    formatter={(v) => formatEUR(v)}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Area type="monotone" dataKey="ventas_equipo" name="Ventas equipo"
                    stroke="#C57B67" strokeWidth={2} fill="#C57B67" fillOpacity={0.18} />
                  <Area type="monotone" dataKey="comisiones" name="Comisiones"
                    stroke="#E3A569" strokeWidth={2} fill="#E3A569" fillOpacity={0.18} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Gráfico consultoras */}
          <div className="bg-white border border-[#E5E0D8] rounded-xl p-6">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-[#736F68] font-display">
              Consultoras y activas
            </h3>
            <div className="mt-4 h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={filteredSeries}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#EFEAE1" />
                  <XAxis dataKey="period" stroke="#736F68" fontSize={11} />
                  <YAxis stroke="#736F68" fontSize={11} />
                  <Tooltip
                    contentStyle={{ background: "#fff", border: "1px solid #E5E0D8", borderRadius: 8, fontSize: 12 }}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line type="monotone" dataKey="consultores" name="Total" stroke="#2D2A26" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="activos" name="Activas" stroke="#8A9A86" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Tabla evolutiva por consultora */}
          {periods.length > 0 && (
            <div className="bg-white border border-[#E5E0D8] rounded-xl overflow-hidden">
              <div className="px-6 py-4 border-b border-[#E5E0D8] flex items-center justify-between gap-4 flex-wrap">
                <h3 className="text-sm font-semibold uppercase tracking-wider text-[#736F68] font-display">
                  Evolución por consultora
                </h3>
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Buscar consultora…"
                  className="text-sm border border-[#E5E0D8] rounded-lg px-3 py-1.5 bg-[#F9F8F6] focus:outline-none focus:ring-1 focus:ring-[#C57B67] w-52"
                />
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-[#F9F8F6] text-[10px] uppercase tracking-wider text-[#736F68]">
                    <tr className="text-left">
                      <th className="px-4 py-3 font-semibold sticky left-0 bg-[#F9F8F6] min-w-[160px]">
                        Consultora
                      </th>
                      {periods.map((p) => (
                        <th key={p} className="px-3 py-3 font-semibold text-center min-w-[100px]">{p}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#F2EFEA]">
                    {filteredConsultoras.length === 0 ? (
                      <tr>
                        <td colSpan={periods.length + 1} className="px-4 py-6 text-center text-[#736F68]">
                          Sin resultados
                        </td>
                      </tr>
                    ) : (
                      filteredConsultoras.map((c) => {
                        const salesValues = periods.map((p) => c.periods[p]?.ventas_pessoais ?? null).filter((v) => v !== null);
                        return (
                          <tr key={c.id} className="hover:bg-[#F9F8F6]">
                            <td className="px-4 py-2.5 font-medium text-[#2D2A26] sticky left-0 bg-white hover:bg-[#F9F8F6] truncate max-w-[160px]">
                              {c.nome}
                            </td>
                            {periods.map((p) => {
                              const entry = c.periods[p];
                              return (
                                <td key={p} className="px-3 py-2.5 text-center">
                                  {entry ? (
                                    <div className="space-y-0.5">
                                      <div className="font-mono text-[#2D2A26]">
                                        {formatEUR(entry.ventas_pessoais)}
                                      </div>
                                      <RankPill rank={entry.rank} />
                                    </div>
                                  ) : (
                                    <span className="text-[#D5CFC5]">—</span>
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
              <div className="px-6 py-2.5 border-t border-[#F2EFEA] flex gap-4 text-[10px] text-[#736F68]">
                <span><span className="font-semibold">C</span> Consultora</span>
                <span><span className="font-semibold text-[#92620A]">LJ</span> Líder Júnior</span>
                <span><span className="font-semibold text-[#7C3626]">LE</span> Líder Ejecutiva</span>
                <span><span className="font-semibold text-[#3D6B39]">LM</span> Líder Master</span>
              </div>
            </div>
          )}
        </>
      )}

      {/* Gestión de snapshots */}
      <div className="bg-white border border-[#E5E0D8] rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-[#E5E0D8]">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-[#736F68] font-display">
            Snapshots guardados
          </h3>
        </div>
        <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F9F8F6] text-[10px] uppercase tracking-wider text-[#736F68]">
            <tr className="text-left">
              <th className="px-6 py-3 font-semibold">Periodo</th>
              <th className="px-6 py-3 font-semibold">Etiqueta</th>
              <th className="px-6 py-3 font-semibold">Fecha y hora</th>
              <th className="px-6 py-3 font-semibold text-right">Consultoras</th>
              <th className="px-6 py-3 font-semibold text-right">Ventas equipo</th>
              <th className="px-6 py-3 font-semibold text-right">Comisiones</th>
              <th className="px-6 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#F2EFEA]">
            {[...snapshots].sort((a, b) => {
              const da = a.uploaded_at || a.snapshot_date;
              const db_ = b.uploaded_at || b.snapshot_date;
              if (!da) return 1;
              if (!db_) return -1;
              return new Date(db_) - new Date(da);
            }).map((s) => (
              <tr key={s.id} className="hover:bg-[#F9F8F6]">
                <td className="px-6 py-3 font-medium text-[#2D2A26]">{s.period}</td>
                <td className="px-6 py-3 text-[#736F68] truncate max-w-xs">{s.label || "—"}</td>
                <td className="px-6 py-3 text-[#736F68] font-mono text-xs">
                  {s.uploaded_at
                    ? new Date(s.uploaded_at).toLocaleString("es-ES", {
                        day: "2-digit", month: "2-digit", year: "numeric",
                        hour: "2-digit", minute: "2-digit", timeZone: "Europe/Madrid",
                      })
                    : (s.snapshot_date || "—")}
                </td>
                <td className="px-6 py-3 text-right font-mono">{s.count}</td>
                <td className="px-6 py-3 text-right font-mono">{formatEUR(s.total_ventas_equipo)}</td>
                <td className="px-6 py-3 text-right font-mono text-[#8A9A86]">{formatEUR(s.total_comisiones)}</td>
                <td className="px-6 py-3 text-right">
                  <button
                    onClick={() => handleDelete(s.id, s.period)}
                    className="p-1.5 rounded-md hover:bg-[#B85C5C]/10 text-[#736F68] hover:text-[#B85C5C] transition-colors"
                    data-testid={`delete-snapshot-${s.id}`}
                    title="Eliminar snapshot"
                  >
                    <Trash size={14} weight="bold" />
                  </button>
                </td>
              </tr>
            ))}
            {snapshots.length === 0 && (
              <tr>
                <td colSpan={7} className="px-6 py-8 text-center text-[#736F68]">
                  Sin snapshots guardados.
                </td>
              </tr>
            )}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}
